import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { CampaignStatus } from "schemas/dashboard";
import { Map } from "schemas/functions";

import StyledLink from "components/Styled/Link";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import GoogleConnect from "features/Settings/GoogleConnect";
import { fetcherAuth } from "utils/api";
import { formatDate } from "utils/time";

import InfoBox from "../../SharedComponents/InfoBox";
import PreviewCampaignBrands from "../../SharedComponents/PreviewCampaignBrands";
import StepperFooter from "../../SharedComponents/StepperFooter";
import styles from "./styles";

interface PreviewTemplate {
  id: number;
  name: string;
  contentType: string;
  previewSubject?: string;
  previewBody: string;
}

interface PreviewData {
  previewTemplates: PreviewTemplate[];
  previewBrandName: string;
  brandsCount: number;
}

interface Props {
  handleUpdateCampaign: (body: Map, skipMoveNext?: boolean) => void;
  moveBack: () => void;
  submitLoading?: boolean;
  errorMessage?: string;
}

export default function CampaignReview({
  handleUpdateCampaign,
  moveBack,
  submitLoading = false,
  errorMessage = "",
}: Props) {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { campaign } = useContext(NewCampaignContext);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [brandsDialogOpen, setBrandsDialogOpen] = useState(false);

  const { emailHealth, fetchIntegrationHealth } = useContext(
    UserIntegrationsContext,
  );

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    setIsExpanded(false);
  };

  const getPlainTextLength = (html: string) =>
    html.replace(/<[^>]*>/g, "").length;

  const fetchPreview = async () => {
    if (!currentOrg?.id || !campaign?.id) return;
    setLoading(true);
    try {
      const { campaign: data } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaign.id}/draft/preview`,
      );
      setPreviewData({
        previewTemplates: data.previewTemplates,
        previewBrandName: data.previewBrandName,
        brandsCount: data.brandsCount,
      });
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaign?.id && currentOrg?.id) {
      fetchPreview();
      if (!emailHealth) fetchIntegrationHealth(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id, currentOrg?.id, emailHealth]);

  const emailsPerDayWarning = (() => {
    if (
      !campaign?.emailsPerDay ||
      !campaign?.startDate ||
      !campaign?.endDate ||
      !previewData?.brandsCount
    )
      return null;
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    const days = Math.max(
      1,
      Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1,
    );
    const requiredPerDay = previewData.brandsCount / days;
    if (requiredPerDay > campaign.emailsPerDay) {
      return `${previewData.brandsCount} emails over ${days} day${days !== 1 ? "s" : ""} requires ~${Math.ceil(requiredPerDay)} emails/day, but you have set your preference to send ${campaign.emailsPerDay} emails/day. You may get rate-limited. Consider reducing the number of brands in this campaign.`;
    }
    return null;
  })();

  const canLaunch =
    campaign?.status === CampaignStatus.draft ||
    campaign?.status === CampaignStatus.pending_approval;

  const getTitleAndDescription = () => {
    if (errorMessage) {
      return {
        title: errorMessage,
        description: "Please fix this error to proceed",
      };
    }
    if (campaign?.status === CampaignStatus.complete) {
      return {
        title: "Campaign is completed",
        description: "",
      };
    }
    if (campaign?.status === CampaignStatus.in_progress) {
      return {
        title: "Campaign is in progress",
        description: "",
      };
    }
    if (!emailHealth) {
      return {
        title: "Your Google integration is not valid",
        description:
          "Please reconnect your Google account to launch the campaign.",
        button: <GoogleConnect />,
      };
    }
    return {
      title: "Almost there! Review your campaign",
      description:
        "Double check everything looks good before launching your campaign.",
    };
  };

  return (
    <>
      <InfoBox
        icon={
          errorMessage || !emailHealth
            ? "fa-regular fa-circle-exclamation"
            : "fa-regular fa-circle-check"
        }
        title={errorMessage || getTitleAndDescription().title}
        description={getTitleAndDescription().description || ""}
        error={Boolean(errorMessage || !emailHealth)}
        button={getTitleAndDescription().button}
      />

      <Box sx={{ mx: { xs: 2.5, md: 4, maxHeight: 400, overflowY: "auto" } }}>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={32} />
          </Grid>
        ) : (
          <Grid container flexDirection="column" gap={3}>
            <Box sx={styles.card}>
              <Typography sx={{ px: 3, pt: 1.5, fontSize: 16 }}>
                Campaign Details
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={styles.detailsContainer}>
                <Box>
                  <Typography sx={{ fontSize: 13, color: grey[500], mb: 0.5 }}>
                    <Box
                      component="i"
                      className="fa-solid fa-bullseye"
                      sx={{ mr: 0.5 }}
                    />
                    Campaign Name
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                    {campaign?.name || "—"}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, color: grey[500], mb: 0.5 }}>
                    <Box
                      component="i"
                      className="fa-regular fa-calendar"
                      sx={{ mr: 0.5 }}
                    />{" "}
                    Date Range
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                    {formatDate(campaign?.startDate)} –{" "}
                    {formatDate(campaign?.endDate)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {campaign?.timezone && (
                      <Box sx={styles.timezone}>
                        <Box
                          component="i"
                          className="fa-regular fa-clock"
                          sx={{ fontSize: 11 }}
                        />
                        {campaign.timezone}
                      </Box>
                    )}
                    {campaign?.emailsPerDay && (
                      <Box sx={styles.emailPerDay}>
                        <Box
                          component="i"
                          className="fa-regular fa-envelope"
                          sx={{ fontSize: 11 }}
                        />
                        {campaign.emailsPerDay} emails/day
                      </Box>
                    )}
                  </Box>
                  {emailsPerDayWarning &&
                    campaign?.status === CampaignStatus.draft && (
                      <Alert severity="warning" sx={{ mt: 1, fontSize: 13 }}>
                        {emailsPerDayWarning}
                      </Alert>
                    )}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, color: grey[500], mb: 0.5 }}>
                    <Box
                      component="i"
                      className="fa-solid fa-users"
                      sx={{ mr: 0.5 }}
                    />{" "}
                    Target Brands
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                      {previewData?.brandsCount ?? "—"}
                    </Typography>
                    {campaign?.campaignBrands &&
                      campaign.campaignBrands.length > 0 && (
                        <Button
                          size="small"
                          onClick={() => setBrandsDialogOpen(true)}
                          sx={{
                            textTransform: "none",
                            fontSize: 12,
                            color: "purple.dark",
                            p: 0,
                            minWidth: 0,
                          }}
                        >
                          View selected brands
                        </Button>
                      )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Block 2: Email preview */}
            {previewData && previewData.previewTemplates.length > 0 && (
              <Box sx={styles.card}>
                <Typography sx={{ px: 3, pt: 1.5, fontSize: 16 }}>
                  Preview Email
                </Typography>
                <Typography
                  sx={{ px: 3, fontSize: 13, color: grey[500], mt: 0.5 }}
                >
                  Example for {previewData.previewBrandName}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ px: 3, pb: 3 }}>
                  {(() => {
                    const template = previewData.previewTemplates[activeIndex];
                    const isLong =
                      getPlainTextLength(template.previewBody) > 200;

                    return (
                      <>
                        {template.previewSubject && (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={styles.subject}>SUBJECT</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                              {template.previewSubject}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ position: "relative" }}>
                          <Typography sx={styles.contentType}>
                            {template?.contentType}{" "}
                            {template?.contentType ===
                            TemplateContentType.FOLLOWUP
                              ? `#${activeIndex}`
                              : ""}{" "}
                          </Typography>
                          <Box
                            sx={styles.emailBodyStyles(isLong, isExpanded)}
                            dangerouslySetInnerHTML={{
                              __html: template.previewBody.replaceAll(
                                `<p style="margin: 0;">`,
                                `<p>`,
                              ),
                            }}
                          />
                          {isLong && !isExpanded && (
                            <Box sx={styles.viewMoreOverlay} />
                          )}
                        </Box>

                        {isLong && (
                          <StyledLink
                            onClick={() => setIsExpanded((prev) => !prev)}
                          >
                            {isExpanded ? "View less" : "View more"}
                          </StyledLink>
                        )}

                        {previewData.previewTemplates.length > 1 && (
                          <Box sx={styles.previewTemplateContainer}>
                            {previewData.previewTemplates.map((_, i) => (
                              <Box
                                key={i}
                                onClick={() => goToSlide(i)}
                                sx={styles.template(i, activeIndex)}
                              />
                            ))}
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            )}
          </Grid>
        )}
      </Box>

      <Dialog
        open={brandsDialogOpen}
        onClose={() => setBrandsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
          }}
        >
          Selected Brands
          <IconButton size="small" onClick={() => setBrandsDialogOpen(false)}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <PreviewCampaignBrands />
        </DialogContent>
      </Dialog>

      <StepperFooter
        handleBack={moveBack}
        handleNext={
          !canLaunch
            ? undefined
            : async () => {
                if (!canLaunch) return;
                await handleUpdateCampaign({
                  status: CampaignStatus.in_progress,
                });
              }
        }
        nextLoading={submitLoading}
        nextDisabled={!canLaunch || !emailHealth}
        nextWording={"Launch Campaign"}
        nextIcon={"fa-solid fa-stars"}
      />
    </>
  );
}
